// SPA-compatible version of Navigation for Vite
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { logout } from '../redux/slices/authSlice';
import { setCurrentTeam } from '../redux/slices/dataSlice';
import type { AppDispatch, RootState } from '../redux/store';
import { useTranslation } from 'react-i18next';
import { SERVER_REGION } from '../../i18n';
import TeamSwitcher from './TeamSwitcher';
import LanguageSwitcher from './LanguageSwitcher';

export default function NavigationSPA() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('nav');
  
  const [isSubFromStorage, setIsSubFromStorage] = useState(false);
  const [accountTypeFromStorage, setAccountTypeFromStorage] = useState<'regular' | 'master' | 'sub'>('regular');
  const [tokenFromStorage, setTokenFromStorage] = useState<boolean | null>(null);

  useEffect(() => {
    setIsSubFromStorage(localStorage.getItem('isSubAccount') === 'true');
    setAccountTypeFromStorage((localStorage.getItem('accountType') as 'regular' | 'master' | 'sub') || 'regular');
    setTokenFromStorage(!!localStorage.getItem('GrainGuardianToken'));
  }, []);

  const isSubFromRedux = useSelector((state: RootState) => state.auth.isSubAccount);
  const accountTypeFromRedux = useSelector((state: RootState) => state.auth.accountType);
  const tokenFromRedux = useSelector((state: RootState) => state.auth.authenticate);

  const isSubAccount = isSubFromRedux || isSubFromStorage;
  const accountType = accountTypeFromRedux || accountTypeFromStorage;
  const hasToken = tokenFromRedux || tokenFromStorage;

  const handleLogout = async () => {
    await dispatch(logout());
    dispatch(setCurrentTeam(null));
    
    // Small delay to ensure Redux state clears
    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 100);
  };

  const isActive = (path: string) => pathname === path;

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <img 
                src="/icon.png" 
                alt="SmartProbe Logo" 
                width={32}
                height={32}
                className="h-8 w-8"
              />
              <span className="ml-2 text-xl font-bold text-gray-900">{t('logo')}</span>
            </div>
            {hasToken && (
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
                    isActive('/dashboard')
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  {t('dashboard')}
                </button>
                <button
                  onClick={() => navigate('/session')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
                    isActive('/session')
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  {t('session')}
                </button>
                {!isSubAccount && (
                  <button
                    onClick={() => navigate('/data-download')}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
                      isActive('/data-download')
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    {t('dataDownload')}
                  </button>
                )}
                {(accountType === 'master' || accountType === 'regular') && !isSubAccount && (
                  <button
                    onClick={() => navigate('/teams')}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
                      isActive('/teams') || pathname.startsWith('/teams/')
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    {t('teams')}
                  </button>
                )}
                <button
                  onClick={() => navigate('/account')}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer ${
                    isActive('/account')
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
                  }`}
                >
                  {t('account')}
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {hasToken && <TeamSwitcher />}
            {SERVER_REGION === 'CN' && <LanguageSwitcher />}
            {hasToken && (
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer"
              >
                {t('logout')}
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

